/** Little helpers for walking module files, which are all raw byte soup. */
export class ByteReader {
  readonly view: DataView;
  readonly bytes: Uint8Array;
  pos = 0;

  constructor(buffer: ArrayBuffer | Uint8Array) {
    if (buffer instanceof Uint8Array) {
      this.bytes = buffer;
      this.view = new DataView(buffer.buffer, buffer.byteOffset, buffer.byteLength);
    } else {
      this.bytes = new Uint8Array(buffer);
      this.view = new DataView(buffer);
    }
  }

  get length(): number {
    return this.bytes.length;
  }

  get remaining(): number {
    return this.bytes.length - this.pos;
  }

  seek(pos: number): void {
    this.pos = pos;
  }

  skip(n: number): void {
    this.pos += n;
  }

  u8(): number {
    return this.bytes[this.pos++] ?? 0;
  }

  i8(): number {
    const v = this.u8();
    return v > 127 ? v - 256 : v;
  }

  u16le(): number {
    const v = this.pos + 1 < this.bytes.length ? this.view.getUint16(this.pos, true) : 0;
    this.pos += 2;
    return v;
  }

  u16be(): number {
    const v = this.pos + 1 < this.bytes.length ? this.view.getUint16(this.pos, false) : 0;
    this.pos += 2;
    return v;
  }

  u32le(): number {
    const v = this.pos + 3 < this.bytes.length ? this.view.getUint32(this.pos, true) : 0;
    this.pos += 4;
    return v;
  }

  i32le(): number {
    const v = this.pos + 3 < this.bytes.length ? this.view.getInt32(this.pos, true) : 0;
    this.pos += 4;
    return v;
  }

  /** Fixed-width string, NUL/space trimmed, non-printables dropped. */
  str(len: number): string {
    let out = '';
    for (let i = 0; i < len; i++) {
      const c = this.bytes[this.pos + i] ?? 0;
      if (c === 0) continue;
      out += c >= 32 && c < 127 ? String.fromCharCode(c) : ' ';
    }
    this.pos += len;
    return out.replace(/\s+$/, '');
  }

  /** Peek a fixed-width string without moving the cursor. */
  peekStr(offset: number, len: number): string {
    const save = this.pos;
    this.pos = offset;
    const s = this.str(len);
    this.pos = save;
    return s;
  }

  sub(len: number): Uint8Array {
    const clamped = Math.max(0, Math.min(len, this.remaining));
    const out = this.bytes.subarray(this.pos, this.pos + clamped);
    this.pos += len;
    return out;
  }
}

/** Growable little-endian writer, used by the MOD/XM savers. */
export class ByteWriter {
  private buf: Uint8Array;
  private len = 0;

  constructor(initial = 1 << 16) {
    this.buf = new Uint8Array(initial);
  }

  private ensure(extra: number): void {
    if (this.len + extra <= this.buf.length) return;
    let size = this.buf.length * 2;
    while (size < this.len + extra) size *= 2;
    const next = new Uint8Array(size);
    next.set(this.buf.subarray(0, this.len));
    this.buf = next;
  }

  get length(): number {
    return this.len;
  }

  u8(v: number): void {
    this.ensure(1);
    this.buf[this.len++] = v & 0xff;
  }

  u16le(v: number): void {
    this.ensure(2);
    this.buf[this.len++] = v & 0xff;
    this.buf[this.len++] = (v >> 8) & 0xff;
  }

  u16be(v: number): void {
    this.ensure(2);
    this.buf[this.len++] = (v >> 8) & 0xff;
    this.buf[this.len++] = v & 0xff;
  }

  u32le(v: number): void {
    this.ensure(4);
    this.buf[this.len++] = v & 0xff;
    this.buf[this.len++] = (v >> 8) & 0xff;
    this.buf[this.len++] = (v >> 16) & 0xff;
    this.buf[this.len++] = (v >> 24) & 0xff;
  }

  /** ASCII, padded with NULs (or spaces) to exactly `len` bytes. */
  str(s: string, len: number, pad = 0): void {
    this.ensure(len);
    for (let i = 0; i < len; i++) {
      const c = i < s.length ? s.charCodeAt(i) : pad;
      this.buf[this.len++] = c < 256 ? c : 63; // '?'
    }
  }

  raw(data: Uint8Array): void {
    this.ensure(data.length);
    this.buf.set(data, this.len);
    this.len += data.length;
  }

  /** Overwrite 4 bytes at an earlier offset — used to backfill chunk sizes. */
  patchU32le(offset: number, v: number): void {
    this.buf[offset] = v & 0xff;
    this.buf[offset + 1] = (v >> 8) & 0xff;
    this.buf[offset + 2] = (v >> 16) & 0xff;
    this.buf[offset + 3] = (v >> 24) & 0xff;
  }

  finish(): Uint8Array {
    return this.buf.slice(0, this.len);
  }
}
